/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import * as React from "react";
import {useEffect, useMemo, useRef, useState} from "react";
import {BottomButtonsWrapper} from "./styled";
import ArgosButton from "../../../../../components/Buttons/ArgosButton";
import ArgosFlexbox from "../../../../../components/Containers/ArgosFlexbox";
import {useAppSelector} from "../../../../../../redux/hooks";
import {selectGlobalDialogState} from '../../../../../../redux/ducks/GlobalDialog/slice';
import {useHasWriteAccess} from "../../../../../../redux/ducks/AccessControl/accessControlHelpers";

import {Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, TextField,} from '@mui/material';
import {usePostRequest} from "../../../../../../api/helper";
import CameraPerformanceSection from "./CollapsibleTabs/CameraPerformanceSection";
import ImagerPeroperties from "./CollapsibleTabs/ImagerProperties";
import ObjectProperties from "./CollapsibleTabs/ObjectProperties"
import ArgosCollapseComponent from "../../../../../components/CollapseComponent/ArgosCollapseComponent";
import {theme} from "../../../../../../constants/theme";
import {getCameraPlotEndpoint} from "../../../constants";
import VAR_COMMON_CONSTANTS from "../../../../../../constants/globalVariables";
import {groupObjectsByTypeNameVersionProfile} from "../../../../radar/SensorRadar/ItemDetails/RadarPerformance/CollapsibleTabs/ObjectProperties";
import {addObjectIdToBaselineData} from "../../../../radar/SensorRadar/ItemDetails/RadarPerformance/Content";
import {StringParam, useQueryParam} from "use-query-params";

const buildPlotData = (_data) => {
  const data = {x: [], y: []}
  const columns = {xLabel: "", yLabel: "Distance (m)"}
  const returnObj = {data, columns}
  const rows = _data?.rows
  const cols = _data?.headers
  rows.forEach((row, i) => {
    const x = row[0]
    returnObj.data.x.push(x)
    const y = row[4]
    returnObj.data.y.push(y)
  })

  const x = cols[0]
  returnObj.columns.xLabel = "Azimuth Angle (°)"

  return returnObj
}

/**
 * Runs when a vertical tab is clicked. Renders a flexbox with a left and right panel. The area to the right of the vertical
 * tabs is this component.
 */
export const Content = (props) => {

  const hasWriteAccess = useHasWriteAccess()
  const [baselineData, setBaselineData] = React.useState(props.baselineData)
  const [lockAR, setLockAR] = React.useState(false);
  const [plotData, setPlotData] = useState({})
  const [fovAndBoresightCameraValues, setFovandBoresightCameraValues] = useState({});
  const [objectData, setObjectData] = useState(props.objectData);
  const [objectDataMap, setObjectDataMap] = useState(props.objectDataMap);
  const [openSavePopup, setOpenSavePopup] = React.useState(false);
  const [openDeletePopup, setOpenDeletePopup] = React.useState(false);
  const [isLoadingPlot, setIsLoadingPlot] = React.useState(false);
  const [configName, setConfigName] = React.useState("");
  const gmid = props.gmid
  const isDialogOpen = useAppSelector(selectGlobalDialogState).isOpen
  const [isError, setIsError] = useState(false);
  const [componentDataMap, setComponentDataMap] = useState({});

  const [sensorId, setSensorId] = useQueryParam('id', StringParam)

  useEffect(() => {
    if (!baselineData) return
    setConfigName(baselineData?.['configName'])
  }, [baselineData?.['configName']]);

  useEffect(() => {
    setBaselineData(props.baselineData)
    setObjectData(props.objectData);
    setObjectDataMap(props.objectDataMap);
  }, [props])

  useEffect(() => {
    handleUpdatePlot(props.baselineData);
  }, [props.configIndex, sensorId])

  function calcSelectDimension(selectDimension, orientationWidth, orientationHeight) {
    let value;
    if (selectDimension === 'Short') { //choose lower value
      value = orientationHeight < orientationWidth ? orientationHeight : orientationWidth;

    } else { // Long - choose higher value
      value = orientationHeight > orientationWidth ? orientationHeight : orientationWidth;
    }
    return value;
  }

  function calcAspectRatio(hPixels, vPixels) {
    return (vPixels / hPixels);
  }

  function setHorizontalPixelsAR(horizontalPixels) {
    const newData = {
      ...baselineData,
      "horizontalPixels": horizontalPixels
    }
    if (lockAR) {
      newData["verticalPixels"] = (horizontalPixels * baselineData.aspectRatio)
    } else {
      newData["aspectRatio"] = calcAspectRatio(horizontalPixels, baselineData.verticalPixels);
    }
    newData["resolutionMP"] = (horizontalPixels * newData["verticalPixels"]) / 1000000
    setBaselineData(newData)

  }

  function setVerticalPixelsAR(verticalPixels) {
    const newData = {
      ...baselineData,
      "verticalPixels": verticalPixels
    }

    if (lockAR) {
      newData["horizontalPixels"] = (verticalPixels / baselineData.aspectRatio);
    } else {
      newData["aspectRatio"] = calcAspectRatio(baselineData.horizontalPixels, verticalPixels);
    }
    newData["resolutionMP"] = (newData["horizontalPixels"] * verticalPixels) / 1000000
    setBaselineData(newData)
  }

  function getBaselineObject() {
    const baselineObjects = objectData.filter((item) => (item.type == "Baseline"))
    const latestVersion = baselineObjects.reduce(function (prev, current) {
      return (prev.version > current.version) ? prev : current
    })
    const baselineObject = {
      "version": latestVersion.version,
      "id": latestVersion.id,
      "links": latestVersion.links,
      "pointsOnObject": latestVersion.pointsOnObject,
      "pixelsAcrossShortDimension": latestVersion.pixelsAcrossShortDimension,
      // "rcs": latestVersion.rcs,
      "heightVertical": latestVersion.heightVertical,
      "widthHorizontal": latestVersion.widthHorizontal,
      "type": "Baseline",
      "name": latestVersion.name,
      "globalId": latestVersion.globalId,
      "isPublicConfig": true,
    }
    return baselineObject
  }

  function getLatestVersion(category, name) {
    let allVersions;
    if (category == "Sign") {
      allVersions = objectData.filter((item) => (item.type == category && item.signName == name))
    } else {
      allVersions = objectData.filter((item) => (item.type == category && item.objectName == name))
    }
    const latestVersion = allVersions.reduce(function (prev, current) {
      return (prev.latestVersion > current.latestVersion) ? prev : current
    })
    return latestVersion
  }

  function handleObjectCategoryChange(e) {
    const baselineObject = getBaselineObject()
    if (e.target.value == "Baseline") {
      const newData = {
        ...baselineData,
        "objectCategory": e.target.value,
        "objectName": baselineObject.name,
        "objectVersion": baselineObject.version,
        "orientationWidth": baselineObject.widthHorizontal,
        "orientationHeight": baselineObject.heightVertical,
        "pixelCount": baselineObject.pixelsAcrossShortDimension,
        "dimensionValue": calcSelectDimension(baselineData.selectDimension, "0.66", "1.5")
      }
      setBaselineData(newData)
    } /*else if (e.target.value === "Potentially Moving Object" || e.target.value === "Miscellaneous") {
      const newData = {
        ...baselineData,
        "objectCategory": e.target.value,
        "objectName": "",
        "objectVersion": "",
        "orientationWidth": "",
        "orientationHeight": "",
        "pixelCount": "",
        "dimensionValue": "",
      }
      setBaselineData(newData)
    }*/ else {
      const newData = {
        ...baselineData,
        "objectCategory": e.target.value,
        "objectName": "",
        "objectVersion": "",
        "orientationWidth": "",
        "orientationHeight": "",
        "pixelCount": "",
        "dimensionValue": "",
      }
      setBaselineData(newData)
    }
  }

  /**
   * Handles the change event for the object name selection, updating the baseline data with the latest version details.
   *
   * @function
   * @param {Object} e - The event object from the name selection change.
   * @param {string} e.target.value - The selected object name.
   * @returns {void}
   * @throws {Error} Logs an error if required data is missing or improperly structured.
   *
   * @description
   * Updates the `baselineData` state with the relevant details of the latest version based on the object's category:
   * - For "Potentially Moving Object," includes radar cross-section values (`rcsSide`, `rcsRear`, `rcsFront`) and orientation dimensions.
   * - For "Sign," includes the horizontal and vertical sizes of the first sign variant.
   *
   * @example
   * // Example usage:
   * handleObjectNameChange(event);
   */
  function handleObjectNameChange(e) {
    const latestVersion = getLatestVersion(baselineData.objectCategory, e.target.value)
    let newData;
    if (baselineData.objectCategory === "Potentially Moving Object" || baselineData.objectCategory === "Miscellaneous") {
      newData = {
        ...baselineData,
        objectVariant: '',
        "objectName": e.target.value,
        "objectVersion": latestVersion.latestVersion,
        "orientationWidth": 0,
        "orientationHeight": 0,
        "pixelCount": 0,
        "dimensionValue": 0
      }
    } else if (baselineData.objectCategory === "Sign") {
      newData = {
        ...baselineData,
        objectVariant: '',
        "objectName": e.target.value,
        "objectVersion": latestVersion.latestVersion,
        "orientationWidth": 0,
        "orientationHeight": 0,
        "pixelCount": 0,
        "dimensionValue": 0
      }
    }
    setBaselineData(newData)
  }

  /**
   * Update the object id based on the selected object.
   */
  const groupedObjects = useMemo(() => groupObjectsByTypeNameVersionProfile(props.objectData), [props.objectData])
  const refGroupedObjects = useRef(groupedObjects)
  useEffect(() => {
    refGroupedObjects.current = groupedObjects
  }, [groupedObjects])
  useEffect(() => {
    const updatedBaselineData = addObjectIdToBaselineData(baselineData, refGroupedObjects)
    setBaselineData(updatedBaselineData)
  }, [baselineData.objectName, baselineData.objectVersion, baselineData.objectVariant, groupedObjects]);

  /**
   * Handles changes to the profile data based on the selected object category
   * and dimensions, updating the baseline data accordingly.
   *
   * @param {Event} e - The event object triggered by the profile change.
   * @param {Object} dimensions - An object containing dimensions for the profile.
   * @param {number} [dimensions.width] - The width dimension, if applicable.
   * @param {number} [dimensions.height] - The height dimension, if applicable.
   *
   * @returns {void} - Updates the baseline data with the new profile information.
   *
   * - If the `objectCategory` in `baselineData` is "Potentially Moving Object",
   *   calculates positional sizes using `getPosSizes` and updates the profile
   *   with the target value, positional sizes, and dimensions.
   *
   * - If the `objectCategory` is "Sign" and dimensions are provided or undefined,
   *   uses the passed dimensions or baseline values to update the profile
   *   with the appropriate properties.
   *
   * - Logs a warning if the `objectCategory` is not handled.
   */
  function handleProfileChange(e, dimensions) {
    let newData;
    if (baselineData.objectCategory === "Potentially Moving Object" || baselineData.objectCategory === 'Miscellaneous') {
      const pmoSizes = getPmoSizes(
        baselineData.objectCategory,
        baselineData.objectName,
        baselineData.objectVersion,
        e.target.value
      );
      if (!pmoSizes) {
        return;
      }
      newData = {
        ...baselineData,
        objectVariant: e.target.value,
        orientationWidth: pmoSizes?.orientationWidth,
        orientationHeight: pmoSizes?.orientationHeight,
        pixelCount: "20", // Defaulting to 20 
        dimensionValue: calcSelectDimension(
          baselineData.selectDimension,
          pmoSizes?.orientationWidth,
          pmoSizes?.orientationHeight
        ),
      };
    } else if (baselineData.objectCategory === "Sign" && dimensions !== undefined) {
      // Handle the case for "Sign" using the passed dimensions
      newData = {
        ...baselineData,
        objectId: dimensions?.id,
        objectVariant: e.target.value,
        orientationWidth: dimensions?.width || baselineData.orientationWidth,
        orientationHeight: dimensions?.height || baselineData.orientationHeight,
        pixelCount: "20", // Defaulting to 20 
        dimensionValue: calcSelectDimension(
          baselineData.selectDimension,
          dimensions?.width,
          dimensions?.height
        ),
      };
    } else {
      console.warn("Unhandled object category:", baselineData.objectCategory);
      return;
    }
    setBaselineData(newData);
  }

  function handleObjectVersionChange(e) {
    const newData = {
      ...baselineData,
      "objectVersion": e.target.value,
    }
    setBaselineData(newData)
  }

  /**
   * Retrieves the orientation width and height for a specific object variant based on category, name, version, and variant.
   *
   * @function
   * @param {string} category - The category of the object (e.g., "Potentially Moving Object").
   * @param {string} name - The name of the object (e.g., "objectName" or "signName").
   * @param {string} version - The version of the object.
   * @param {string} variant - The variant identifier for the object.
   * @returns {Object|null} An object containing `orientationWidth` and `orientationHeight` or `null` if not found.
   * @throws {Error} Logs an error if:
   * - No matching object is found based on category, name, and version.
   * - The variant is not found in the object's parameters.
   * - Orientation dimensions are not found for the variant.
   *
   * @example
   * // Example usage:
   * const dimensions = getPmoSizes("Potentially Moving Object", "Stop", "v1.0", "VariantA");
   * if (dimensions) {
   *     console.log(dimensions.orientationWidth, dimensions.orientationHeight);
   * }
   */
  function getPmoSizes(category, name, version, variant) {
    // Map category to the correct key for name
    const nameKey = category === "Potentially Moving Object" || category === 'Miscellaneous' ? "objectName" : "signName";
    // Find the correct object based on category, name, and version
    const matchingObject = objectData.find(
      item =>
        item.type === category &&
        item[nameKey] === name &&
        item.latestVersion === version
    );
    if (!matchingObject) {
      console.error("No matching object found");
      return null;
    }

    // Access the correct profile based on the variant based on schema structure 
    const normalizedVariant = variant
      .toLowerCase()
      .replace(/(?:^\w|[\s-]\w)/g, match => match.toUpperCase())
      .replace(/\s+/g, "")
      .replace(/^./, match => match.toLowerCase());

    const profile = matchingObject.parameters?.[normalizedVariant];

    if (!profile) {
      console.error(`Variant ${variant} not found in parameters`);
      return null;
    }

    // Extract orientation width and height
    const {orientationWidth, orientationHeight} = profile;

    if (orientationWidth == null || orientationHeight == null) {
      console.error("Orientation dimensions not found");
      return null;
    }

    return {
      orientationWidth,
      orientationHeight,
    };
  }

  function calcVerticalSize(orientationWidth, orientationHeight, selectDimension) {
    if (baselineData.objectCategory == "Custom") {
      const newData = {
        ...baselineData,
        "orientationHeight": orientationHeight
      }
      if (orientationWidth != undefined) {
        newData["dimensionValue"] = calcSelectDimension(selectDimension, orientationWidth, orientationHeight);
      }
      setBaselineData(newData);
    }
  }

  function calcHorizontalSize(orientationWidth, orientationHeight, selectDimension) {
    if (baselineData.objectCategory == "Custom") {
      const newData = {
        ...baselineData,
        "orientationWidth": orientationWidth
      }
      if (orientationHeight != undefined) {
        newData["dimensionValue"] = calcSelectDimension(selectDimension, orientationWidth, orientationHeight);
      }

      setBaselineData(newData);
    }
  }

  function calcPixelCount(pixelCount) {
    /*const newData = {
      ...baselineData,
      "pixelsAcrossShortDimension":  e.target.value
    }*/
    setBaselineData({...baselineData, "pixelCount": pixelCount});
  }

  function handleReset() {
    setBaselineData(props.baselineData)
  }

  const url = getCameraPlotEndpoint
  const updatePlotHandle = usePostRequest(url)

  async function handleUpdatePlot(baselineData) {
    const newPlot = {
      "objectId": baselineData.objectId,
      "resolution": baselineData.resolution,
      "resolutionMP": baselineData.resolutionMP,
      "horizontalPixels": baselineData.horizontalPixels,
      "aspectRatio": baselineData.aspectRatio,
      "verticalPixels": baselineData.verticalPixels,
      "lockAR": lockAR,
      "objectCategory": baselineData.objectCategory,
      "orientationWidth": baselineData.orientationWidth,
      "orientationHeight": baselineData.orientationHeight,
      "pixelCount": baselineData.pixelCount,
      "selectDimension": baselineData.selectDimension.toLowerCase(),
      "dimensionValue": baselineData.dimensionValue,
      "lensTable": props.sensorCameraData.plotData.lensTable.rows,
      "imagerTable": props.sensorCameraData.plotData.imagerTable,
      "hfov": props.sensorCameraData.azimuthResponse,
      "vfov": props.sensorCameraData.elevationResponse,
      sensorId
    };
    setIsLoadingPlot(true)
    updatePlotHandle(newPlot).then((responseJson) => {
      setIsError(false)
      const fovAndBoresightCameraObject = {
        'azimuth_fov': responseJson["azimuth_fov_camera"],
        'elevation_fov': responseJson["elevation_fov_camera"],
        'max_range': responseJson["max_range_camera"]
      }
      let x = responseJson['x']
      let y = responseJson['y']
      setPlotData(prevState => {
          const obj = {...prevState}
          obj.data = {x, y}
          return obj
        }
      )
      setFovandBoresightCameraValues(fovAndBoresightCameraObject)
      setIsLoadingPlot(false)
    }).catch(err => {
      console.log(err)
      setIsError(true)
      setIsLoadingPlot(false)
    })
  }

  function handleResolutionChange(e) {
    if (e.target.value != "Custom") {
      setLockAR(false);
    }
    let imagerData = props.sensorCameraData.plotData.imagerTable.concat([{
      "label": "Custom",
      "width": '',
      "height": ''
    }])[props.sensorCameraData.plotData.imagerTable.concat([{
      "label": "Custom",
      "width": '',
      "height": ''
    }]).map(imagerData => imagerData.label).indexOf(e.target.value)]
    const newData = {
      ...baselineData,
      "resolution": e.target.value,
      "horizontalPixels": imagerData.width,
      "verticalPixels": imagerData.height,
      "aspectRatio": imagerData.width == '' || imagerData.height == '' ? 0 : imagerData.height / imagerData.width,
      "resolutionMP": (imagerData.height * imagerData.width) / 1000000
    }
    setBaselineData(newData)
  }


  function handleDimensionChange(e) {
    const newData = {
      ...baselineData,
      "selectDimension": e.target.value,
      "dimensionValue": calcSelectDimension(e.target.value, baselineData.orientationWidth, baselineData.orientationHeight)
    }
    setBaselineData(newData)
  }

  function onSaveConfiguration() {
    props.onSaveConfiguration(null, baselineData, configName, gmid)
  }

  function onOverrideConfiguration() {
    props.onSaveConfiguration(props.configIndex - 1, baselineData, configName, gmid)
  }

  const handleClose = () => {
    setOpenSavePopup(false);
    setOpenDeletePopup(false);
  };
  const handleClickSaveOpen = () => {
    setOpenSavePopup(true);
  };

  function handleSaveConfigName(e) {
    setConfigName(e.target.value)
  }

  function onDeleteConfiguration() {
    props.onDeleteConfiguration(props.baselineData.configName, props.baselineData.owner)
  }

  function processListOfVersions(listOfElements) {
    let local_map = {}
    listOfElements.forEach(item => {
      if (local_map[item.name] !== undefined) {
        local_map[item.name].push(item.version)
      } else {
        local_map[item.name] = []
        local_map[item.name].push(item.version)
      }
    })
    return local_map
  }

  function setIsPublicConfig(_isPublicConfig) {
    const newObject = {
      ...baselineData,
      'isPublicConfig': _isPublicConfig
    }
    setBaselineData(newObject);
  }

  useEffect(() => {
    createCollapsibleTabs()
  }, [props, baselineData, objectDataMap, objectData, plotData, configName, lockAR, sensorId])

  const createCollapsibleTabs = () => {
    let _componentDataMap = {}
    _componentDataMap["Imager Properties"] = <ImagerPeroperties
      baselineData={baselineData}
      sensorCameraData={props?.sensorCameraData}
      handleResolutionChange={handleResolutionChange}
      setHorizontalPixelsAR={setHorizontalPixelsAR}
      setVerticalPixelsAR={setVerticalPixelsAR}
      setLockAR={setLockAR}
      lockAR={lockAR}
    />
    _componentDataMap[VAR_COMMON_CONSTANTS.sensorApp.all.SENSOR_TESTING_CONDITIONS_HEADER] = <ObjectProperties
      baselineData={baselineData}
      handleObjectCategoryChange={handleObjectCategoryChange}
      objectDataMap={objectDataMap}
      objectData={objectData}
      handleObjectNameChange={handleObjectNameChange}
      handleProfileChange={handleProfileChange}
      processListOfVersions={processListOfVersions}
      handleObjectVersionChange={handleObjectVersionChange}
      calcHorizontalSize={calcHorizontalSize}
      calcVerticalSize={calcVerticalSize}
      handleDimensionChange={handleDimensionChange}
      calcPixelCount={calcPixelCount}
    />
    _componentDataMap["Camera Performance"] = <CameraPerformanceSection
      isLoadingPlot={isLoadingPlot}
      isError={isError}
      plotData={plotData}
      fovAndBoresightCameraValues={fovAndBoresightCameraValues}
    />
    setComponentDataMap(_componentDataMap)
  }
  return (
    // flexbox with left and right panel, rendered when clicking a vertical tab
    <ArgosFlexbox vertical style={{overflow: 'auto', height: '100%', width: '100%'}}>
      {/* Buttons Section */}
      <ArgosFlexbox style={{alignContent: 'center'}}>
        <BottomButtonsWrapper style={{marginTop: '0px'}}>
          <ArgosButton disabled={isLoadingPlot} style={{width: '230px', height: '30px'}} type="submit"
                       onClick={() => handleUpdatePlot(baselineData)}>Update Plot</ArgosButton>
          <ArgosButton style={{width: '230px', height: '30px'}} type="submit"
                       onClick={handleReset}>Reset Defaults</ArgosButton>
          {hasWriteAccess && <ArgosButton
            style={{width: '230px', height: '30px'}}
            onClick={handleClickSaveOpen}
          >
            Save Configuration
          </ArgosButton>}
          <Dialog
            open={openSavePopup}
            onClose={handleClose}
          >
            <DialogTitle>
              Save Configuration
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                What would you like to name this configuration?
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                id="name"
                fullWidth
                variant="standard"
                onChange={handleSaveConfigName} value={configName}
              />
            </DialogContent>
            <DialogActions>
              <ArgosButton autoFocus onClick={handleClose}>Cancel</ArgosButton>
              <ArgosButton disabled={props.configIndex == 0} onClick={function (event) {
                onOverrideConfiguration();
                handleClose()
              }}>Override Configuration</ArgosButton>
              <ArgosButton onClick={function (event) {
                onSaveConfiguration();
                handleClose()
              }}>Save as New Configuration</ArgosButton>
            </DialogActions>
          </Dialog>
          {hasWriteAccess &&
            <ArgosButton disabled={props.configIndex == 0 || baselineData?.owner != props.gmid}
                         onClick={onDeleteConfiguration}>Delete Configuration</ArgosButton>}
        </BottomButtonsWrapper>
        <div>
          <FormControlLabel
            control={<Checkbox
              onChange={(e) => {
                setIsPublicConfig(e.target.checked);
              }}
              checked={baselineData?.isPublicConfig} name="isPublicConfig"
              disabled={props.configIndex == 0 || baselineData?.owner != props.gmid}
            />} label="Public Configurations"/>
        </div>
      </ArgosFlexbox>
      <ArgosCollapseComponent
        styleProps={{
          outlineOnly: true,
          fontColor: theme.layout.sidebar.backgroundColor,
        }}
        defaultOpen={true}
        data={componentDataMap ?? {}}
      />
    </ArgosFlexbox>
  )
}
